import classNames from 'classnames'
import { Children, useCallback, useEffect, useRef, useState } from 'react'
import type { CarouselHandle } from '../carousel'
import { Carousel } from '../carousel'
import { PaginationDots } from '../pagination-dots'
import styles from './CarouselSlides.module.scss'
import type { CarouselSlidesProps } from './types'

const computeActiveIndex = (activeSlide: number, slidesNumber: number) => {
  return activeSlide >= 0 && activeSlide < slidesNumber ? activeSlide : 0
}

const CarouselSlides = ({
  className,
  children,
  activeSlide: initialActiveSlide = 0,
  paginationDotsAriaLabel,
  getDotAriaLabel,
  getSlideAriaLabel,
  onNext,
  onPrevious,
  ...rest
}: CarouselSlidesProps) => {
  const slidesNumber = Children.toArray(children).length
  const [activeSlide, setActiveSlide] = useState(
    computeActiveIndex(initialActiveSlide, slidesNumber)
  )
  const carouselRef = useRef<CarouselHandle>(null)

  useEffect(() => {
    setActiveSlide(() => {
      const newIndex = computeActiveIndex(initialActiveSlide, slidesNumber)
      carouselRef.current?.scrollToIndex(newIndex)
      return newIndex
    })
  }, [initialActiveSlide, slidesNumber])

  const handlePrev = useCallback(
    (newIndex?: number) => {
      setActiveSlide((prev) => newIndex ?? prev)
      onPrevious?.(newIndex)
    },
    [onPrevious]
  )

  const handleNext = useCallback(
    (newIndex?: number) => {
      setActiveSlide((prev) => newIndex ?? prev)
      onNext?.(newIndex)
    },
    [onNext]
  )

  const handlePageClick = (index: number) => {
    carouselRef.current?.scrollToIndex(index)
    setActiveSlide(index)
  }
  return (
    <div className={classNames(styles['pintxo-carousel-slides'], className)}>
      <Carousel
        ref={carouselRef}
        withButtons
        {...rest}
        role="tabpanel"
        onNext={handleNext}
        onPrevious={handlePrev}
        getSlideAriaLabel={getSlideAriaLabel}
        nextButtonAriaLabel={'Next slide'}
        previousButtonAriaLabel={'Previous slide'}
      >
        {children}
      </Carousel>
      <PaginationDots
        aria-label={paginationDotsAriaLabel}
        index={activeSlide}
        onClick={handlePageClick}
        pages={slidesNumber}
        getDotAriaLabel={getDotAriaLabel}
      />
    </div>
  )
}

export default CarouselSlides
